import { bizDevelopmentDashboardService } from "../../../services/api/biz-development-dashboard.endpoint";
import { getDateRangeForTimeframe } from "../../../components/BizDevelopment/Dashboard/helpers/bizDevelopmentDashboardHelpers";
import {
  setOverviewAccountSummaryActiveRequestKey,
  setIsOverviewAccountSummaryLoading,
  setOverviewAccountSummaryOutput,
  setOverviewAccountSummaryLoadedRequestKey,
  setIsOverviewConversionsLoading,
  setOverviewConversionsBaseFilterOptionsLoaded,
  setOverviewConversionsLoadedRequestKey,
  setOverviewConversionsNotesLoadedRequestKey,
  setOverviewConversionsOutput,
  setOverviewConversionsFilterOptions,
  setIsOverviewConversionsFilterLoading,
  setIsOverviewConversionsNotesLoading,
  setOverviewConversionsNotes,
  setComparisonFetchedFor,
  setIsLoading,
  setOutputMetadata,
  setQueryOutput,
  setIsComparisonLoading,
  setComparisonQueryOutput,
  setBudgetDataOfBizDevelopment,
  setBudgetMetaDataOfBizDevelopment,
  setIsFetchedBudgetData,
  setIsBeAwareFraudPlayersLoading,
  setBeAwareFraudPlayersOutput,
  setBeAwareFraudPlayersLoadedRequestKey,
  setIsBeAwareNegativeBalanceLoading,
  setBeAwareNegativeBalanceOutput,
  setBeAwareNegativeBalanceLoadedRequestKey,
  setIsBeAwareConversionsUploadLoading,
  setBeAwareConversionsUploadOutput,
  setBeAwareConversionsUploadLoadedRequestKey,
} from "../../../features/bizDevelopmentDashboardSlice";
import {
  BIZ_BE_AWARE_QUERY_IDS,
  BIZ_DEVELOPMENT_DASHBOARD_QUERY_ID,
  BIZ_OVERVIEW_CONVERSIONS_QUERY_IDS,
  LASTTHIRTYDAYSQID,
  LASTTWELVEMONTHQID,
  SIXWEEKQID,
} from "../../../pages/BizDevelopment/Dashboard/QueryIDsBizDevelopmentDashboard";

import store from "../../../store";
import { setDisplayErrorSnackBar } from "../../../features/userSlice";
import dayjs from "dayjs";

export const OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES = {
  lastThirtyDays: "last_30_days",
  sixWeeks: "last_6_weeks",
  lastTwelveMonthsFull: "last_12_months_full",
  lastTwelveMonthsProportional: "last_12_months_proportional",
} as const;

export type OverviewAccountSummaryTimeframe =
  (typeof OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES)[keyof typeof OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES];

export type BizOverviewConversionsDataSet = "action" | "click";

export interface BizOverviewConversionsRequestParams {
  oauthTokenHeader: string;
  dataSet: BizOverviewConversionsDataSet;
  startDate: string;
  endDate: string;
  timeUnit?: string;
  websiteSharedUids: string[];
  dimensions: string[];
  filters?: { key: string; value: string }[];
}

export const buildBizOverviewConversionsParams = ({
  oauthTokenHeader,
  dataSet,
  startDate,
  endDate,
  timeUnit = "day",
  websiteSharedUids,
  dimensions,
  filters = [],
}: BizOverviewConversionsRequestParams) => {
  const queryId =
    dataSet === "click"
      ? BIZ_OVERVIEW_CONVERSIONS_QUERY_IDS.click
      : BIZ_OVERVIEW_CONVERSIONS_QUERY_IDS.action;
  const params: any = {
    oauthTokenHeader,
    queryId,
    startDate,
    endDate,
    timeUnit,
    type: "site",
    websiteSharedUids,
    dimensions,
  };
  if (dataSet === "click") {
    const filterMap = filters.reduce(
      (acc: Record<string, string>, curr: { key: string; value: string }) => {
        if (curr.key.toLowerCase() === "trafficsource") {
          acc.trafficSource = curr.value;
        } else if (curr.key.toLowerCase() === "kwid") {
          acc.kwId = curr.value;
        } else {
          acc[curr.key] = curr.value;
        }
        return acc;
      },
      {},
    );
    params.campaignId = filterMap.campaignId || "1";
    params.adgroupId = filterMap.adgroupId || "1";
    params.matchtype = "all";
    params.kwId = filterMap.kwId || "1";
    params.country = filterMap.country || "all";
    params.trafficSource = filterMap.trafficSource || "all";
  }
  return params;
};

export const buildBizOverviewConversionsRequestKey = ({
  dataSet,
  startDate,
  endDate,
  timeUnit = "day",
  websiteSharedUids,
  dimensions,
  filters = [],
}: Omit<BizOverviewConversionsRequestParams, "oauthTokenHeader">) => {
  const filterKey = filters
    .slice()
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((filter) => `${filter.key}:${filter.value}`)
    .join(",");

  return [
    dataSet,
    startDate,
    endDate,
    timeUnit,
    websiteSharedUids.slice().sort().join(","),
    dimensions.slice().sort().join(","),
    filterKey,
  ].join("|");
};

export const buildBizOverviewConversionsNotesRequestKey = ({
  websiteSharedUids,
  fromDate,
  toDate,
}: {
  websiteSharedUids: string[];
  fromDate: string;
  toDate: string;
}) => [fromDate, toDate, websiteSharedUids.slice().sort().join(",")].join("|");

export const buildOverviewAccountSummaryRequestKey = (
  timeframe: OverviewAccountSummaryTimeframe,
  siteSharedUids: string[],
) => `${timeframe}|${siteSharedUids.slice().sort().join(",")}`;

export const buildBeAwareRequestKey = (siteSharedUids: string[]) =>
  siteSharedUids.slice().sort().join(",");

const getOverviewAccountSummaryConfig = (
  timeframe: OverviewAccountSummaryTimeframe,
) => {
  const today = dayjs();
  const format = "YYYY-MM-DD";
  switch (timeframe) {
    case OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES.lastThirtyDays:
      return {
        queryId: LASTTHIRTYDAYSQID,
        startDate: today.subtract(29, "day").format(format),
        endDate: today.format(format),
      };
    case OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES.sixWeeks:
      return {
        queryId: SIXWEEKQID,
        startDate: today.subtract(41, "day").format(format),
        endDate: today.format(format),
      };
    case OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES.lastTwelveMonthsFull:
      return {
        queryId: LASTTWELVEMONTHQID,
        periodType: "Full",
      };
    case OVERVIEW_ACCOUNT_SUMMARY_TIMEFRAMES.lastTwelveMonthsProportional:
      return {
        queryId: LASTTWELVEMONTHQID,
        periodType: "Proportional",
      };
  }
};

async function getDashboardData(
  oauthTokenHeader: string,
  siteSharedUids: string[],
  timeframe: string,
) {
  try {
    store.dispatch(setIsLoading(true));
    const { startDate, endDate } = getDateRangeForTimeframe(timeframe);
    const response = await bizDevelopmentDashboardService.getDashboardData(
      oauthTokenHeader,
      BIZ_DEVELOPMENT_DASHBOARD_QUERY_ID,
      startDate,
      endDate,
      siteSharedUids,
    );
    if (!response || !response.status || response.status !== 200) {
      throw new Error("Failed to fetch biz development dashboard data");
    } else {
      store.dispatch(setQueryOutput(response?.data?.data?.query_output ?? []));
      store.dispatch(
        setOutputMetadata(response?.data?.data?.output_metadata ?? {}),
      );
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setIsLoading(false));
  }
}

async function getComparisonDashboardData(
  oauthTokenHeader: string,
  siteSharedUids: string[],
  timeframe: string,
) {
  try {
    store.dispatch(setIsComparisonLoading(true));
    const { startDate, endDate } = getDateRangeForTimeframe(timeframe);
    const response = await bizDevelopmentDashboardService.getDashboardData(
      oauthTokenHeader,
      BIZ_DEVELOPMENT_DASHBOARD_QUERY_ID,
      startDate,
      endDate,
      siteSharedUids,
    );
    if (!response || !response.status || response.status !== 200) {
      throw new Error(
        "Failed to fetch biz development dashboard comparison data",
      );
    } else {
      store.dispatch(
        setComparisonQueryOutput(response?.data?.data?.query_output ?? []),
      );
      store.dispatch(setComparisonFetchedFor(timeframe));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setIsComparisonLoading(false));
  }
}

// Budget Data
async function getBizDevelopmentBudgetData({
  oauthTokenHeader,
  queryId,
  websiteSharedUids,
  type,
  dimension,
}: {
  oauthTokenHeader: string;
  queryId: string;
  websiteSharedUids: string[];
  type: string;
  dimension: string[];
}) {
  try {
    store.dispatch(setIsLoading(true));
    store.dispatch(setIsFetchedBudgetData(false));
    const response = await bizDevelopmentDashboardService.getBizBudgetData({
      oauthTokenHeader,
      queryId,
      websiteSharedUids,
      type,
      dimension,
    });
    if (!response || !response.status) {
      throw new Error("Failed to fetch biz development budget data");
    } else if (response.status === 200) {
      store.dispatch(
        setBudgetDataOfBizDevelopment(response?.data?.data?.query_output ?? []),
      );
      store.dispatch(
        setBudgetMetaDataOfBizDevelopment(
          response?.data?.data?.output_metadata ?? {},
        ),
      );
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unable to fetch budget data",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setIsLoading(false));
    store.dispatch(setIsFetchedBudgetData(true));
  }
}

async function getOverviewAccountSummaryData(
  oauthTokenHeader: string,
  siteSharedUids: string[],
  timeframe: OverviewAccountSummaryTimeframe,
) {
  const requestKey = buildOverviewAccountSummaryRequestKey(
    timeframe,
    siteSharedUids,
  );

  try {
    store.dispatch(setOverviewAccountSummaryActiveRequestKey(requestKey));
    store.dispatch(setIsOverviewAccountSummaryLoading(true));
    const config = getOverviewAccountSummaryConfig(timeframe);
    const response =
      await bizDevelopmentDashboardService.getDashboardDataOverview({
        oauthTokenHeader,
        queryId: config.queryId,
        websiteSharedUids: siteSharedUids,
        type: "site",
        dimension: ["bbb_uk"],
        start_date: config.startDate,
        end_date: config.endDate,
        period_type: config.periodType,
      });
    if (!response || !response.status || response.status !== 200) {
      throw new Error(
        "Failed to fetch biz development overview account summary data",
      );
    } else {
      const activeRequestKey =
        store.getState().bizDevelopmentDashboard
          .overviewAccountSummaryActiveRequestKey;
      if (activeRequestKey !== requestKey) return;

      store.dispatch(
        setOverviewAccountSummaryOutput(
          response?.data?.data?.query_output ?? [],
        ),
      );
      store.dispatch(setOverviewAccountSummaryLoadedRequestKey(requestKey));
    }
  } catch (e: any) {
    const activeRequestKey =
      store.getState().bizDevelopmentDashboard
        .overviewAccountSummaryActiveRequestKey;
    if (activeRequestKey === requestKey) {
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || "Unknown error occurred",
          type: "error",
        }),
      );
    }
  } finally {
    const activeRequestKey =
      store.getState().bizDevelopmentDashboard
        .overviewAccountSummaryActiveRequestKey;
    if (activeRequestKey === requestKey) {
      store.dispatch(setIsOverviewAccountSummaryLoading(false));
      store.dispatch(setOverviewAccountSummaryActiveRequestKey(null));
    }
  }
}

async function getOverviewConversionsData(
  params: BizOverviewConversionsRequestParams,
) {
  try {
    store.dispatch(setIsOverviewConversionsLoading(true));
    const response =
      await bizDevelopmentDashboardService.getOverviewConversionsData(
        buildBizOverviewConversionsParams(params),
      );
    if (!response?.status || response.status !== 200) {
      throw new Error("Failed to fetch biz development overview conversions");
    }
    store.dispatch(
      setOverviewConversionsOutput(response?.data?.data?.query_output ?? []),
    );
    store.dispatch(
      setOverviewConversionsLoadedRequestKey(
        buildBizOverviewConversionsRequestKey(params),
      ),
    );
    return response?.data?.data;
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || e?.message || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setIsOverviewConversionsLoading(false));
  }
}

async function getOverviewConversionsBaseFilterOptions(
  oauthTokenHeader: string,
) {
  try {
    const [countriesResponse, trafficSourcesResponse] = await Promise.all([
      bizDevelopmentDashboardService.getOverviewConversionsCountries(
        oauthTokenHeader,
      ),
      bizDevelopmentDashboardService.getOverviewConversionsTrafficSources(
        oauthTokenHeader,
      ),
    ]);

    if (countriesResponse?.status === 200) {
      store.dispatch(
        setOverviewConversionsFilterOptions({
          key: "country",
          data: countriesResponse?.data?.data ?? [],
        }),
      );
    }
    if (trafficSourcesResponse?.status === 200) {
      store.dispatch(
        setOverviewConversionsFilterOptions({
          key: "trafficSource",
          data: trafficSourcesResponse?.data?.data ?? [],
        }),
      );
    }
    store.dispatch(setOverviewConversionsBaseFilterOptionsLoaded(true));
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText ||
          "Failed to fetch biz development conversion filter options",
        type: "error",
      }),
    );
  }
}

async function getOverviewConversionsDependentFilterOptions({
  oauthTokenHeader,
  websiteSharedUids,
  dimensions,
  trafficSource,
}: {
  oauthTokenHeader: string;
  websiteSharedUids: string[];
  dimensions: string[];
  trafficSource: string;
}) {
  try {
    store.dispatch(setIsOverviewConversionsFilterLoading(true));
    const baseParams = {
      oauthTokenHeader,
      type: "site",
      websiteSharedUids,
      dimensions,
      trafficSource,
    };
    const [campaignResponse, adgroupResponse, keywordResponse] =
      await Promise.all([
        bizDevelopmentDashboardService.getOverviewConversionsFilterOptions(
          BIZ_OVERVIEW_CONVERSIONS_QUERY_IDS.campaignFilter,
          baseParams.oauthTokenHeader,
          baseParams.type,
          baseParams.websiteSharedUids,
          baseParams.dimensions,
          baseParams.trafficSource,
          1,
          "all",
        ),
        bizDevelopmentDashboardService.getOverviewConversionsFilterOptions(
          BIZ_OVERVIEW_CONVERSIONS_QUERY_IDS.adgroupFilter,
          baseParams.oauthTokenHeader,
          baseParams.type,
          baseParams.websiteSharedUids,
          baseParams.dimensions,
          baseParams.trafficSource,
        ),
        bizDevelopmentDashboardService.getOverviewConversionsFilterOptions(
          BIZ_OVERVIEW_CONVERSIONS_QUERY_IDS.keywordFilter,
          baseParams.oauthTokenHeader,
          baseParams.type,
          baseParams.websiteSharedUids,
          baseParams.dimensions,
          baseParams.trafficSource,
        ),
      ]);

    if (campaignResponse?.status === 200) {
      store.dispatch(
        setOverviewConversionsFilterOptions({
          key: "campaign",
          data: campaignResponse?.data?.data?.query_output ?? [],
        }),
      );
    }
    if (adgroupResponse?.status === 200) {
      store.dispatch(
        setOverviewConversionsFilterOptions({
          key: "adgroup",
          data: adgroupResponse?.data?.data?.query_output ?? [],
        }),
      );
    }
    if (keywordResponse?.status === 200) {
      store.dispatch(
        setOverviewConversionsFilterOptions({
          key: "keyword",
          data: keywordResponse?.data?.data?.query_output ?? [],
        }),
      );
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message:
          e?.parsedText ||
          "Failed to fetch biz development conversion dependent filters",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setIsLoading(false));
    store.dispatch(setIsFetchedBudgetData(true));
    store.dispatch(setIsOverviewConversionsFilterLoading(false));
  }
}

async function getOverviewConversionsActivityNotes({
  oauthTokenHeader,
  websiteSharedUids,
  fromDate,
  toDate,
}: {
  oauthTokenHeader: string;
  websiteSharedUids: string[];
  fromDate: string;
  toDate: string;
}) {
  try {
    store.dispatch(setIsOverviewConversionsNotesLoading(true));
    const response =
      await bizDevelopmentDashboardService.getOverviewConversionsActivityNotes(
        oauthTokenHeader,
        websiteSharedUids,
        fromDate,
        toDate,
      );
    if (response?.status === 204) {
      store.dispatch(setOverviewConversionsNotes([]));
      store.dispatch(
        setOverviewConversionsNotesLoadedRequestKey(
          buildBizOverviewConversionsNotesRequestKey({
            websiteSharedUids,
            fromDate,
            toDate,
          }),
        ),
      );
      return [];
    }
    if (!response?.status || response.status !== 200) {
      throw new Error("Failed to fetch biz development conversion notes");
    }
    const notes = Object.values(response?.data?.data || {}).flat();
    store.dispatch(setOverviewConversionsNotes(notes));
    store.dispatch(
      setOverviewConversionsNotesLoadedRequestKey(
        buildBizOverviewConversionsNotesRequestKey({
          websiteSharedUids,
          fromDate,
          toDate,
        }),
      ),
    );
    return notes;
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || e?.message || "Failed to load activity notes",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setIsOverviewConversionsNotesLoading(false));
  }
}

// Be Aware - Fraud Players
async function getBeAwareFraudPlayersData(
  oauthTokenHeader: string,
  siteSharedUids: string[],
) {
  const requestKey = buildBeAwareRequestKey(siteSharedUids);
  try {
    store.dispatch(setIsBeAwareFraudPlayersLoading(true));
    const response =
      await bizDevelopmentDashboardService.getBeAwareFraudPlayersData(
        oauthTokenHeader,
        BIZ_BE_AWARE_QUERY_IDS.fraudPlayers,
        siteSharedUids,
      );
    if (!response || !response.status || response.status !== 200) {
      throw new Error("Failed to fetch biz development fraud players data");
    } else {
      store.dispatch(
        setBeAwareFraudPlayersOutput(response?.data?.data?.query_output ?? []),
      );
      store.dispatch(setBeAwareFraudPlayersLoadedRequestKey(requestKey));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setIsBeAwareFraudPlayersLoading(false));
  }
}

// Be Aware - Negative Balance
async function getBeAwareNegativeBalanceData(
  oauthTokenHeader: string,
  siteSharedUids: string[],
) {
  const requestKey = buildBeAwareRequestKey(siteSharedUids);
  try {
    store.dispatch(setIsBeAwareNegativeBalanceLoading(true));
    const response =
      await bizDevelopmentDashboardService.getBeAwareNegativeBalanceData(
        oauthTokenHeader,
        BIZ_BE_AWARE_QUERY_IDS.negativeBalance,
        siteSharedUids,
      );
    if (!response || !response.status || response.status !== 200) {
      throw new Error("Failed to fetch biz development negative balance data");
    } else {
      store.dispatch(
        setBeAwareNegativeBalanceOutput(
          response?.data?.data?.query_output ?? [],
        ),
      );
      store.dispatch(setBeAwareNegativeBalanceLoadedRequestKey(requestKey));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setIsBeAwareNegativeBalanceLoading(false));
  }
}

// Be Aware - Conversions Upload
async function getBeAwareConversionsUploadData(
  oauthTokenHeader: string,
  siteSharedUids: string[],
) {
  const requestKey = buildBeAwareRequestKey(siteSharedUids);
  // start_date = today - 60 days, end_date = today - 2 days
  // (e.g. today 2026-07-20 -> start 2026-05-21, end 2026-07-18).
  const today = dayjs();
  const dateFormat = "YYYY-MM-DD";
  const startDate = today.subtract(60, "day").format(dateFormat);
  const endDate = today.subtract(2, "day").format(dateFormat);
  try {
    store.dispatch(setIsBeAwareConversionsUploadLoading(true));
    const response =
      await bizDevelopmentDashboardService.getBeAwareConversionsUploadData(
        oauthTokenHeader,
        BIZ_BE_AWARE_QUERY_IDS.conversionsUpload,
        siteSharedUids,
        startDate,
        endDate,
      );
    if (!response || !response.status || response.status !== 200) {
      throw new Error(
        "Failed to fetch biz development conversions upload data",
      );
    } else {
      store.dispatch(
        setBeAwareConversionsUploadOutput(
          response?.data?.data?.query_output ?? [],
        ),
      );
      store.dispatch(setBeAwareConversionsUploadLoadedRequestKey(requestKey));
    }
  } catch (e: any) {
    store.dispatch(
      setDisplayErrorSnackBar({
        message: e?.parsedText || "Unknown error occurred",
        type: "error",
      }),
    );
  } finally {
    store.dispatch(setIsBeAwareConversionsUploadLoading(false));
  }
}

export const BizDevelopmentDashboardEndPointLogic = {
  getDashboardData,
  getComparisonDashboardData,
  getBizDevelopmentBudgetData,
  getOverviewAccountSummaryData,
  getOverviewConversionsData,
  getOverviewConversionsBaseFilterOptions,
  getOverviewConversionsDependentFilterOptions,
  getOverviewConversionsActivityNotes,
  getBeAwareFraudPlayersData,
  getBeAwareNegativeBalanceData,
  getBeAwareConversionsUploadData,
};
